/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable no-undef */
/* eslint-disable no-console */
import { KeyCode } from '../../utils/constants';
import SelectorEngine from '../../utils/dom/selector-engine';
import BaseComponent from '../BaseComponent';
import EventHandler from '../../utils/dom/event-handler';
import Manipulator from '../../utils/dom/manipulator';
import { startLoadingAnimation } from '../../utils/dom/loading';
import { loadPlacesLibrary } from '../../utils/google-maps-loader';

export const GEOLOCATION_CHANGE_EVENT = 'geolocation_change';

const LOCATION_INPUT_NAME = 'location';
const SEARCH_OPTION_LOCATION = 'loc';
const SEARCH_ERROR_DELAY = 300;
const INVALIDCLASS = 'is-invalid';

const ERROR_TYPES = {
	SERVICE: 'service',
	RESULTS: 'results',
	MINIMUM_CHARACTERS: 'MINIMUM_CHARACTERS',
	ZERO_RESULTS: 'ZERO_RESULTS'
}

const TYPE_ERROR_MESSAGES = {
	'service': 'We are currently experiencing problems with the location search. Please use an alternative search or filter method',
	'results': 'There are no matches with this search term. Please use an alternative location',
	'MINIMUM_CHARACTERS': 'Please enter at least 3 characters'
};

/**
 * @param {object} [options] - Optional attributes when performing a search
 * @param {string} [options.date] - The day being searched on
 * @param {number} [options.latitude] - Degrees east/west of the prime meridian
 * @param {number} [options.longitude] - Degrees north/south of the equator
 * @param {string} [options.location] - The name of the place searched for
 * @param {string} [options.timeRange] - The time window being searched within
 * @returns {string} - An updated URL for the current page that reflects the search parameters
 */
const generateSearchUrl = (options = {}) => {
	const optionalParameters = Object.fromEntries(
		Object.entries(options).filter(([_key, value]) => !!value || value === 0)
	);
	const query = new URLSearchParams(optionalParameters);
	return `${window.location.origin}${window.location.pathname}?${query}`;
};

const handleLocationError = (navigator) => {
	/* eslint-disable no-alert */
	if (!navigator) {
		alert('Your browser does not support geolocation. Please choose a location from the list to move on.');
	} else {
		alert('There was an unexpected error. We can not access your current location.');
	}
	/* eslint-enable no-alert */
};

class LocationSearch extends BaseComponent {
	constructor(element) {
		super(element);
		this.position = undefined;
		this.cookies = undefined;
		this.useGeolocation = element.classList.contains('use-geolocation');
		this.init();
	}

	static get NAME() {
		return LocationSearch.name;
	}

	init() {
		this.searchWrapper = SelectorEngine.findOne('.js-lta-location-search-wrapper', this._element);
		this.resultsWrapper = SelectorEngine.findOne('.lta-location-search-results', this._element);
		this.closeButton = SelectorEngine.findOne('.js-lta-location-search-wrapper > button', this._element);
		this.errorWrapper = SelectorEngine.findOne('.lta-location-search-error', this._element);
		this.searchInput = SelectorEngine.findOne('.js-location-search-field-input', this._element);
		this.searchTypeSelect = SelectorEngine.findOne('.js-location-search-field-select select', this._element);
		this.submit = SelectorEngine.findOne(
			'.js-lta-location-search-wrapper + .lta-search-field__search-button',
			this._element
		);
		this.locationWrapper = SelectorEngine.findOne('.js-lta-location-search', this._element);
		this.clear = SelectorEngine.findOne('.lta-location-search__clear', this._element);
		this.hasError = false;
		this.searchingLocation = true;
		this.initializeCloseButton();
		this.customEvent = new Event('after_change');
		if (this.clear) this.clear.classList.add('u-hidden');
		this.bookingCourt = false;
		this.placeChanged = false;
		this.autocomplete= null;
		this.autocompleteLoading = false;

		this.strictSearch = Manipulator.getDataAttribute(this._element, 'strictSearch');

		// For support for Phase 3 Find Court Location search form with different markup

		if (this.searchWrapper.classList.contains('lta-court-results')) {
			this.bookingCourt = true;
			this.submit = SelectorEngine.findOne('.lta-btn.lta-court-booking__find-court', this._element.closest('form'));
		}

		// Find current selected option on load

		if (this.searchTypeSelect && this.searchTypeSelect.value !== SEARCH_OPTION_LOCATION) {
			this.searchingLocation = false;
			this.toggleLocationSearch(false);

			// On load if not location change name attribute on input
			const selected = this.searchTypeSelect.options[this.searchTypeSelect.selectedIndex],
				placeholder = selected.getAttribute('data-placeholder'),
				text = selected.text,
				nameV = text.toLowerCase().replace(' ', '-');

			this.searchInput.name = nameV;
			if (this.searchInput.placeholder != placeholder) {
				this.searchInput.placeholder = placeholder;
			}
		}

		EventHandler.on(this.clear, 'click', () => {
			this._element?.classList.remove(INVALIDCLASS);
			this.clearPosition();
		});

		// Try HTML5 geolocation.
		// If not coordinates in URL
		// And if not other search is made

		// eslint-disable-next-line promise/catch-or-return
		this.initCookiesLib().then(() => this.initGeolocation());

		// After checking which search we are in initiliaze search and events on input

		this.doSearch();

		EventHandler.on(this.searchTypeSelect, 'change', (event) => {
			const self = event.target,
				selected = self.options[self.selectedIndex],
				value = selected.value,
				placeholder = selected.getAttribute('data-placeholder'),
				text = selected.text,
				nameV = text.toLowerCase().replace(' ', '-');

			this.searchInput.name = nameV;
			if (this.searchInput.placeholder != placeholder) {
				this.searchInput.placeholder = placeholder;
			}
			if (value === SEARCH_OPTION_LOCATION) {
				if (!this.searchingLocation) {
					this.searchingLocation = true;
					this.doSearch();
				}
				this.toggleLocationSearch(true);
			} else {
				this.toggleLocationSearch(false);
			}
			this.resetSearch();
		});

		EventHandler.on(this.submit, 'click', (event) => {
			event.preventDefault();
			this.strictSearch = Manipulator.getDataAttribute(this.searchWrapper, 'strictSearch');
			if (
				this.searchInput.name == LOCATION_INPUT_NAME &&
				(!this.searchInput.value || (this.strictSearch && !this.placeChanged))
			) {
				this._element?.classList.add(INVALIDCLASS);
				return;
			}

			if (this.searchInput.value.length < 3) {
				// Show error message and set focus to the search input
				this.showError(ERROR_TYPES.MINIMUM_CHARACTERS);
				return;
			}

			if (this.searchInput.name == LOCATION_INPUT_NAME && !this.hasError && this.position) {
				const locationInputValue = this.searchInput.value;
				const shouldRedirect = this._element.classList.contains('no-submit');
				if (this.bookingCourt) {
					const dateInput = SelectorEngine.findOne('.lta-court-booking input.duet-date__input');
					const timeInput = SelectorEngine.findOne('.lta-court-booking__time-search select');
					const ageInput = SelectorEngine.findOne('.lta-court-booking__age-group select');
					const classTypeInput = SelectorEngine.findOne('.lta-court-booking__class-type select');
					const classDayInput = SelectorEngine.findOne('.lta-court-booking__class-day select');

					const selectedTimeInput = timeInput.options[timeInput.selectedIndex];
					const selectedClassDayInput = classDayInput?.options[classDayInput.selectedIndex];
					const classTypeInputObject = {
						classType:
							classTypeInput?.selectedOptions?.length > 0
								? classTypeInput.options[classTypeInput.selectedOptions].value
								: undefined
					};
					const classDayInputObject = {
						classDay: selectedClassDayInput ? selectedClassDayInput.value : undefined
					};
					this.updatePage(this.position, {
						location: locationInputValue,
						date: dateInput?.value,
						timeRange: selectedTimeInput ? selectedTimeInput.value : undefined,
						ageGroup:
							ageInput?.selectedOptions?.length > 0 ? ageInput.options[ageInput.selectedOptions].value : undefined,
						...classTypeInputObject,
						...classDayInputObject,
					},  shouldRedirect);
				} else {
					this.updatePage(this.position, { location: locationInputValue }, shouldRedirect);
				}
			} else if (this.searchInput.name == 'coach-name' && !this.hasError) {
				this.updatePageExceptLocation({
					'optType': 'name',
					'coach-name': this.searchInput.value
				});
			} else if (this.searchInput.name == 'coach-code' && !this.hasError) {
				this.updatePageExceptLocation({
					'optType': 'code',
					'coach-code': this.searchInput.value
				});
			} else if (this.searchInput.name == 'course-name' && !this.hasError) {
				this.updatePageExceptLocation({
					'optType': 'name',
					'course-name': this.searchInput.value
				});
			}
		});

		// Plain DOM listener: the Maps API is lazy-loaded and not available at init time
		EventHandler.on(this.searchInput, 'keydown', (event) => {
			if (event.keyCode === KeyCode.Enter && SelectorEngine.findOne('.pac-container:not([style*="display: none"]')) {
				this.placeChanged = true;
				this._element?.classList.remove(INVALIDCLASS);
				this.resetErrors();
				if (event.preventDefault) {
					event.preventDefault();
				} else {
					event.cancelBubble = true;
					event.returnValue = false;
				}
			}
			if(this.searchInput?.value?.length >= 3) {
				this.resetErrors();
			}
		});
	}

	clearPosition() {
		this.position = undefined;
	}

	setPosition({ latitude, longitude }) {
		this.position = { latitude, longitude };
	}

	initCookiesLib() {
		return import('js-cookie')
			.then(({ default: Cookies }) => {
				this.cookies = Cookies;
				return this.cookies;
			})
			.catch((error) => {
				console.error(error);
			});
	}

	/**
	 * @param {object} coordinates - The central point around which to search
	 * @param {number} coordinates.latitude - Degrees east/west of the prime meridian
	 * @param {number} coordinates.longitude - Degrees north/south of the equator
	 * @param {object} [options] - Optional attributes when performing a search
	 * @param {string} [options.date] - The day being searched on
	 * @param {string} [options.location] - The name of the place searched for
	 * @param {string} [options.timeRange] - The time window being searched within
	 * @returns {void}
	 */
	updatePage(coordinates, options = {}, redirect = true) {
		if (coordinates) {
			startLoadingAnimation(!redirect);
			if (redirect) {
				window.location = generateSearchUrl({ ...coordinates, ...options });
			}
		} else {
			console.log('Cannot search for results without coordinates being provided');
		}
	}

	updatePageExceptLocation(options = {}) {
		if (options) {
			startLoadingAnimation();
			window.location = generateSearchUrl({ ...options });
		} else {
			console.log('Cannot search for results without coordinates being provided');
		}
	}

	initGeolocation() {
		const searchExists = location.search.length,
			foundParameters = this.getPositionParamsFromQueryString();

		let prefs = this.cookies.get('cookieprefs');

		const geolocationPopUp = () => {
			const cookie = this.cookies.get('currentLocation');
			const cookiePrefs = JSON.parse(this.cookies.get('cookieprefs'));
			const functionCookie = prefs ? cookiePrefs.func : false;
			// eslint-disable-next-line unicorn/no-this-assignment
			const self = this; // TODO fix bad practice: https://github.com/sindresorhus/eslint-plugin-unicorn/blob/v37.0.1/docs/rules/no-this-assignment.md

			if (functionCookie) {
				// 3 states for the cookie
				// Either it doesn't exist but we can set it since functionality category is accepted for cookies
				// Denied if we have asked but are not given permission to use the current location
				// 0 if the browser doesn't support the use of geolocation
				if (!cookie) {
					// If we can store cookie but there isn't one already
					if (navigator.geolocation) {
						navigator.permissions
							.query({ name: 'geolocation' })
							.then(function (result) {
								if (result.state == 'granted' || result.state == 'prompt') {
									navigator.geolocation.getCurrentPosition(
										(position) => {
											self.position = {
												latitude: position.coords.latitude,
												longitude: position.coords.longitude
											};
											self.cookies.set('currentLocation', JSON.stringify(self.position), { expires: 90 });
											self.updatePage(position.coords);
										},
										() => {
											self.cookies.set('currentLocation', 'denied', { expires: 90 });
											//handleLocationError(true);
										}
									);
								} else if (result.state == 'denied') {
									// Set the cookie to empty string.The access to location is denied and we shouldn't bother the user with multiple attempts
									self.cookies.set('currentLocation', 'denied', { expires: 90 });
								}
								return 1;
							})
							.catch((error) => {
								console.error(
									'Error: ' + error.message + ' found while waiting for the navigator permission state return.'
								);
							});
					} else {
						// Browser doesn't support Geolocation
						this.cookies.set('currentLocation', '0', { expires: 90 });
						handleLocationError(false);
					}
				} else if (cookie != 0 && cookie != 'denied') {
					const json = JSON.parse(decodeURIComponent(cookie));
					self.updatePage(json);
				}
			} else {
				// We don't have permission to set a cookie for storing current location but we still try to access that once everytime the user reloads
				if (navigator.geolocation) {
					navigator.permissions
						.query({ name: 'geolocation' })
						.then(function (result) {
							if (result.state == 'granted' || result.state == 'prompt') {
								navigator.geolocation.getCurrentPosition((position) => {
									self.updatePage(position.coords);
								});
							}
							return 1;
						})
						.catch((error) => {
							console.error(
								'Error: ' + error.message + ' found while waiting for the navigator permission state return.'
							);
						});
				} else {
					// Browser doesn't support Geolocation
					handleLocationError(false);
				}
			}
		};

		if (!foundParameters && !searchExists && this.useGeolocation) {
			// If the attempt for currentLocation isn't already made
			// And if funtionality cookies are accepted
			if (prefs) {
				geolocationPopUp();
			} else {
				EventHandler.on(window, 'cookiesAcceptance', () => {
					prefs = this.cookies.get('cookieprefs');
					geolocationPopUp();
				});
			}
		}
	}

	addLoader() {
		document.body.className = 'lta-loader in-out-animation';
	}

	getPositionParamsFromQueryString() {
		const queryString = new URLSearchParams(location.search);

		if (queryString.get('latitude')) {
			const latitude = queryString.get('latitude');
			const longitude = queryString.get('longitude');
			this.setPosition({ latitude, longitude });
			return true;
		}
		return false;
	}

	toggleLocationSearch(bool) {
		const container = SelectorEngine.findOne('.pac-container');
	
		if (bool) {
			// Enable location search
			this.searchingLocation = true;
	
			//Reinitialize autocomplete if missing or container was removed
			if (this.autocomplete && !document.querySelector('.pac-container')) {
				this.disposeAutocomplete();
			}
			this.initiateLocationSearch();
		} else {
			// Switch away from location search
			this.searchingLocation = false;
	
			// Remove pac-container
			if (container) container.remove();
			this.disposeAutocomplete();
		}
	}

	disposeAutocomplete() {
		// Detach the widget before dropping the reference so it stops sending
		// Places requests for the input
		if (this.autocomplete && window.google?.maps?.event) {
			window.google.maps.event.clearInstanceListeners(this.autocomplete);
			window.google.maps.event.clearInstanceListeners(this.searchInput);
		}
		this.autocomplete = undefined;
	}

	initializeCloseButton() {
		EventHandler.on(this.closeButton, 'click', () => {
			this.resetSearch();
			this.searchInput.focus();
		});
	}

	initiateLocationSearch() {
		// Only ever hold one live Autocomplete per component: stacked instances
		// each open their own Places billing session on the same input
		if (this.autocomplete || this.autocompleteLoading) return;

		const inputs = SelectorEngine.find('.js-location-search-field-input', this._element);

		if (!inputs || inputs.length === 0) return;

		this.autocompleteLoading = true;

		loadPlacesLibrary()
			.then(({ Autocomplete }) => {
				this.autocompleteLoading = false;

				// User switched away from location search while the library was loading
				if (!this.searchingLocation) return this.autocomplete;

				const options = {
					componentRestrictions: { country: ['gb', 'im', 'je', 'gg'] },
					fields: ['geometry']
				};

		for (const input of inputs) {
			const autocomplete = new Autocomplete(input, options);
			this.autocomplete = autocomplete;

			autocomplete.addListener('place_changed', () => {
				const place = autocomplete.getPlace();
				this.placeChanged = true;
				this._element?.classList.remove(INVALIDCLASS);

				if (!place.geometry || !place.geometry.location) {
					// User entered the name of a Place that was not suggested and
					// pressed the Enter key, or the Place Details request failed
					return;
				}

				const geoCoordinates = {
					lat: place.geometry.location.lat(),
					lng: place.geometry.location.lng()
				};

				this.setPosition({ latitude: geoCoordinates.lat, longitude: geoCoordinates.lng });

				this.searchInput.dispatchEvent(this.customEvent);
				this.resetErrors();
				EventHandler.trigger(this.searchInput, GEOLOCATION_CHANGE_EVENT, geoCoordinates);
			});
		}
		return this.autocomplete;
	})
	.catch((error) => {
		this.autocompleteLoading = false;
		console.error(error);
		this.showError(ERROR_TYPES.SERVICE);
	});
	}

	doSearch(searchValue) {
		if (searchValue === '') {
			this.resetSearch();
			return;
		}

		EventHandler.on(this.searchInput, 'keypress', (event) => {
			const self = event.target;
			if (event.keyCode == KeyCode.Enter && self.name == LOCATION_INPUT_NAME) {
				event.preventDefault();

				EventHandler.one(self, 'after_change', () => {
					this.submit.click();
				});
			}
		});

		if (this.searchingLocation) {
			this.initiateLocationSearch();
		} else {
			return;
		}

		EventHandler.on(this.searchInput, 'input', (event) => {
			this.clearPosition();
			this.resetErrors();

			if (this.searchInput.name == LOCATION_INPUT_NAME && this.placeChanged === false) {
				this.searchWrapper.classList.add('lta-location-search__field--is-searching');
				if (event.target.value.length === 0) {
					this.searchWrapper.classList.remove('lta-location-search__field--is-searching');
				}
				// Google autocomplete is slower than our JS. We need to delay the check.
				setTimeout(() => {
					const value = this.searchInput.value;
					const resultContainer = SelectorEngine.findOne('.pac-container');
					// The container is created asynchronously once the Places library has loaded
					if (
						resultContainer &&
						resultContainer.style.display == 'none' &&
						resultContainer.children.length === 0 &&
						value.length > 4
					) {
						this.showError(ERROR_TYPES.RESULTS);
						this.searchWrapper.classList.add('lta-location-search__field--is-searching');
					}
				}, SEARCH_ERROR_DELAY);
			}
			this.placeChanged = false;
		});
	}

	resetSearch() {
		this.searchInput.value = '';
		Manipulator.toggleClassCond(this.resultsWrapper, 'lta-location-search-results--show-results', false);
		Manipulator.toggleClassCond(this.searchWrapper, 'lta-location-search-wrapper--has-error', false);
		this.closeButton.classList.remove('lta-btn--show-btn');
		this.resetErrors();

		// Reset URL
		window.history.pushState('', '', window.location.origin + window.location.pathname);
	}

	resetErrors() {
		if (this.hasError) {
			Manipulator.toggleClassCond(this.resultsWrapper, 'lta-location-search-results--show-results', false);
			Manipulator.toggleClassCond(this.searchWrapper, 'lta-location-search-wrapper--has-error', false);
		}
		this.hasError = false;
	}

	showError(type) {
		const paragraph = SelectorEngine.findOne('p', this.errorWrapper);
		let message;
		switch (type) {
			case ERROR_TYPES.SERVICE:
			default:
				message = TYPE_ERROR_MESSAGES.service;
				break;
			case ERROR_TYPES.RESULTS:
			case ERROR_TYPES.ZERO_RESULTS:
				message = TYPE_ERROR_MESSAGES.results;
				break;
			case ERROR_TYPES.MINIMUM_CHARACTERS:
				message = TYPE_ERROR_MESSAGES.MINIMUM_CHARACTERS;
				break;
		}

		paragraph.innerHTML = message;
		Manipulator.toggleClassCond(this.resultsWrapper, 'lta-location-search-results--show-results', true);
		Manipulator.toggleClassCond(this.searchWrapper, 'lta-location-search-wrapper--has-error', true);
		this.hasError = true;
	}
}

export default LocationSearch;
